import type { BcStoreUserRecord } from '../services/api';

export interface PermissionMetricRow {
  permissionCode: string;
  accessType: 'read' | 'write' | 'unknown';
  isWrite: boolean;
  userCount: number;
  users: string[];
}

export interface PermissionMetricsSummary {
  uniquePermissions: number;
  writePermissions: number;
  readPermissions: number;
  unknownPermissions: number;
  usersWithWriteAccess: number;
  usersWithNoPermissions: number;
}

export interface PermissionMetrics {
  rows: PermissionMetricRow[];
  summary: PermissionMetricsSummary;
}

/**
 * Pivot user-permission rows into permission-centric metrics.
 */
export function buildPermissionMetrics(users: BcStoreUserRecord[]): PermissionMetrics {
  const byCode = new Map<string, PermissionMetricRow>();
  const usersWithWrite = new Set<string>();
  let usersWithNoPermissions = 0;

  for (const user of users) {
    if (user.permissions.length === 0) {
      usersWithNoPermissions += 1;
      continue;
    }

    let userHasWrite = false;
    for (const permission of user.permissions) {
      if (permission.isWrite) {
        userHasWrite = true;
      }

      const existing = byCode.get(permission.code);
      if (existing) {
        existing.userCount += 1;
        existing.users.push(user.email);
      } else {
        byCode.set(permission.code, {
          permissionCode: permission.code,
          accessType: permission.accessType,
          isWrite: permission.isWrite,
          userCount: 1,
          users: [user.email],
        });
      }
    }

    if (userHasWrite) {
      usersWithWrite.add(user.id);
    }
  }

  const rows = Array.from(byCode.values()).sort((a, b) => {
    if (b.userCount !== a.userCount) {
      return b.userCount - a.userCount;
    }
    return a.permissionCode.localeCompare(b.permissionCode);
  });

  return {
    rows,
    summary: {
      uniquePermissions: rows.length,
      writePermissions: rows.filter((row) => row.isWrite).length,
      readPermissions: rows.filter((row) => row.accessType === 'read').length,
      unknownPermissions: rows.filter((row) => row.accessType === 'unknown').length,
      usersWithWriteAccess: usersWithWrite.size,
      usersWithNoPermissions,
    },
  };
}
